/**
 * OS HUD — the persistent, always-on bar pinned to the top of every in-app
 * screen (Home, all app/OS pages). Read-only chrome: it never logs. The points
 * balance comes from the dashboard read (useDashboardData); the ["dashboard"]
 * query-key prefix is invalidated by the workout save mutation, so the HUD
 * updates after a log.
 *
 * Layout, left → right:
 *   "Gamification" wordmark (→ Home)  ·····  points in gold + PTS (→ Score)  ·
 *   gift (→ Rewards, turns gold on the Rewards page).
 *
 * The streak was intentionally dropped: a single max-across-categories number
 * misrepresents 7 distinct streaks. Streaks live in full on the Score page.
 *
 * Blue-tinted bar on dark: eclair --primary (#597eff) at low alpha for bg/border
 * (bg-primary/10, border-primary/40), so it tracks the token and stays in step
 * with active nav, links, and the focus ring.
 * Flat — no shadow, no gradient. Numbers use the tabular `.num` utility.
 */
import { Link, useLocation } from "react-router-dom";
import { Gift } from "lucide-react";

import { useDashboardData } from "@/features/dashboard/useDashboardData";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";

export function OsHud() {
  const { pathname } = useLocation();
  const { balance, isLoading } = useDashboardData();

  const onRewards = pathname === "/rewards";
  const points = balance?.point_balance ?? null;

  return (
    <div className="flex items-center gap-2 rounded-[10px] border border-primary/40 bg-primary/10 px-3 py-2">
      {/* brand → Home */}
      <Link to="/" aria-label="Home" className="flex items-center">
        <span className="text-[13px] font-extrabold tracking-tight text-foreground">Gamification</span>
      </Link>

      {/* Points → Score (grouped on the right with rewards) */}
      <Link to="/score" aria-label="Score" className="ml-auto flex items-center gap-1">
        {isLoading || points === null ? (
          <Skeleton className="h-4 w-10" />
        ) : (
          <span className="num text-[15px] font-bold leading-none text-reward">{points.toLocaleString()}</span>
        )}
        <span className="text-[9px] font-semibold tracking-[0.04em] text-muted-foreground">PTS</span>
      </Link>

      {/* Gift → Rewards (turns gold on the Rewards page) */}
      <Link to="/rewards" aria-label="Rewards" className={cn(onRewards ? "text-reward" : "text-muted-foreground")}>
        <Gift className="size-[19px]" strokeWidth={1.8} fill={onRewards ? "currentColor" : "none"} />
      </Link>
    </div>
  );
}
